import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { API, formatDate } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ForkKnife, CheckCircle } from "@phosphor-icons/react";

const TAGES = ["Nudeln mit Pistaziensoße", "Caesar Salad mit Hähnchenbruststreifen"];
const A_LA_CARTE = [
  "Hausgemachte Käsespätzle mit Bio-Eiern",
  "Schnitzel Wiener Art mit Pommes",
  "Cheeseburger mit Pommes",
  "Spaghetti Aglio e Olio",
  "Nudeln mit Tomatensauce",
  "Spaghetti Bolognese",
  "Nudeln mit Pesto",
  "Nudeln mit Thunfischsauce",
];

export default function Meal() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState(false);
  const [form, setForm] = useState({
    first_name: "", last_name: "", persons: 1, room_note: "",
    date: "", time: "12:30", meal_type: "Mittagessen",
    tagesgericht: "", a_la_carte: "",
    unvertraeglichkeiten: "", bemerkungen: "",
  });

  useEffect(() => {
    axios.get(`${API}/public/guest/${token}`).then(r => {
      setCtx(r.data);
      setForm(f => ({
        ...f,
        first_name: r.data.guest.first_name || "",
        last_name: r.data.guest.last_name || "",
        persons: r.data.booking.guests || 1,
        room_note: r.data.booking.room,
        date: r.data.booking.check_in,
      }));
    }).catch(e => setErr(e.response?.data?.detail || "Ungültiger Link"));
  }, [token]);

  const submit = async () => {
    if (!form.tagesgericht && !form.a_la_carte) {
      setErr("Bitte wählen Sie ein Gericht aus."); return;
    }
    try {
      await axios.post(`${API}/public/meal/${token}`, form);
      setOk(true);
    } catch (e) { setErr(e.response?.data?.detail || "Fehler beim Absenden"); }
  };

  if (err && !ctx) return <div className="min-h-screen flex items-center justify-center p-6"><div className="msb-card max-w-md">{err}</div></div>;
  if (!ctx) return null;
  if (ok) return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="msb-card max-w-md text-center">
        <CheckCircle size={64} weight="duotone" className="mx-auto text-primary"/>
        <h2 className="text-2xl font-bold mt-3" style={{fontFamily:"'Work Sans',sans-serif"}}>{form.meal_type} bestellt</h2>
        <p className="text-muted-foreground mt-2">Wir bereiten Ihr Gericht am {formatDate(form.date)} um {form.time} Uhr vor.</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen py-10 px-4 bg-background">
      <div className="max-w-2xl mx-auto space-y-6">
        <div className="text-center">
          <div className="inline-flex items-center gap-2 text-primary">
            <ForkKnife size={28} weight="duotone"/>
            <span className="font-bold text-lg" style={{fontFamily:"'Work Sans',sans-serif"}}>MainSpessartBlick</span>
          </div>
          <h1 className="text-3xl font-bold mt-2" style={{fontFamily:"'Work Sans',sans-serif"}}>Mittag- oder Abendessen</h1>
          <p className="text-muted-foreground mt-1">🍽️ 19,99 € pro Person · hausgemacht mit Bio-Zutaten & Liebe</p>
        </div>

        <div className="msb-card text-sm text-muted-foreground">
          <strong className="text-foreground">💡 Wichtiger Hinweis:</strong> Damit wir frisch und in bester Qualität zubereiten können, bitten wir Sie, pro Zimmer <strong>ein gemeinsames Gericht</strong> für alle Personen auszuwählen. 🏡🍽️
        </div>

        <div className="msb-card space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Vorname</Label><Input value={form.first_name} onChange={e=>setForm({...form, first_name: e.target.value})} className="rounded-xl mt-1.5" data-testid="m-first"/></div>
            <div><Label>Nachname</Label><Input value={form.last_name} onChange={e=>setForm({...form, last_name: e.target.value})} className="rounded-xl mt-1.5"/></div>
            <div><Label>Personen</Label><Input type="number" min="1" value={form.persons} onChange={e=>setForm({...form, persons: +e.target.value})} className="rounded-xl mt-1.5"/></div>
            <div><Label>Zimmer / Einheit</Label><Input value={form.room_note} onChange={e=>setForm({...form, room_note: e.target.value})} className="rounded-xl mt-1.5"/></div>
            <div><Label>Datum</Label><Input type="date" value={form.date} onChange={e=>setForm({...form, date: e.target.value})} className="rounded-xl mt-1.5" data-testid="m-date"/></div>
            <div><Label>Uhrzeit</Label><Input type="time" value={form.time} onChange={e=>setForm({...form, time: e.target.value})} className="rounded-xl mt-1.5"/></div>
            <div className="col-span-2">
              <Label>Mahlzeit</Label>
              <Select value={form.meal_type} onValueChange={(v)=>setForm({...form, meal_type: v})}>
                <SelectTrigger className="rounded-xl mt-1.5" data-testid="m-type"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Mittagessen">Mittagessen</SelectItem>
                  <SelectItem value="Abendessen">Abendessen</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <div className="font-semibold text-sm mb-2" style={{fontFamily:"'Work Sans',sans-serif"}}>Tagesgerichte</div>
            <div className="space-y-2">
              {TAGES.map(t => (
                <label key={t} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-muted/40 cursor-pointer">
                  <input type="radio" name="dish" checked={form.tagesgericht===t} onChange={()=>setForm({...form, tagesgericht: t, a_la_carte: ""})} data-testid={`m-tages-${t}`}/>
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <div className="font-semibold text-sm mb-2" style={{fontFamily:"'Work Sans',sans-serif"}}>À-la-carte-Gerichte</div>
            <div className="space-y-2">
              {A_LA_CARTE.map(t => (
                <label key={t} className="flex items-center gap-2 text-sm p-2 rounded-lg hover:bg-muted/40 cursor-pointer">
                  <input type="radio" name="dish" checked={form.a_la_carte===t} onChange={()=>setForm({...form, a_la_carte: t, tagesgericht: ""})} data-testid={`m-alacarte-${t.slice(0,10)}`}/>
                  <span>{t}</span>
                </label>
              ))}
            </div>
          </div>

          <div>
            <Label>Besondere Wünsche oder Unverträglichkeiten</Label>
            <Textarea value={form.unvertraeglichkeiten} onChange={e=>setForm({...form, unvertraeglichkeiten: e.target.value})} className="rounded-xl mt-1.5"/>
          </div>
          <div>
            <Label>Bemerkungen</Label>
            <Textarea value={form.bemerkungen} onChange={e=>setForm({...form, bemerkungen: e.target.value})} className="rounded-xl mt-1.5"/>
          </div>

          {err && <div className="text-sm text-destructive">{err}</div>}

          <Button onClick={submit} className="w-full h-11 rounded-xl bg-primary" data-testid="m-submit">
            Bestellung absenden
          </Button>
        </div>
      </div>
    </div>
  );
}
